import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Pencil, Trash2 } from 'lucide-react';
import { VacationPolicy } from '../../types';

interface PolicyListProps {
  policies: VacationPolicy[];
  loading?: boolean;
  onEdit: (policy: VacationPolicy) => void;
  onDelete: (policy: VacationPolicy) => void;
}

export const PolicyList: React.FC<PolicyListProps> = ({ policies, loading, onEdit, onDelete }) => {
  const { t } = useTranslation('vacations');

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('admin.policies.columns.country')}</TableHead>
            <TableHead>{t('admin.policies.columns.name')}</TableHead>
            <TableHead className="text-center">{t('admin.policies.columns.allowance')}</TableHead>
            <TableHead>{t('admin.policies.columns.accrualType')}</TableHead>
            <TableHead className="text-center">{t('admin.policies.columns.carryOver')}</TableHead>
            <TableHead className="text-right">{t('table.headers.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                {t('table.loading')}
              </TableCell>
            </TableRow>
          ) : policies.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                {t('admin.policies.empty')}
              </TableCell>
            </TableRow>
          ) : (
            policies.map((policy) => (
              <TableRow key={policy.id} data-testid={`policy-row-${policy.id}`}>
                <TableCell className="font-medium">{policy.countryCode}</TableCell>
                <TableCell>{policy.name}</TableCell>
                <TableCell className="text-center">{policy.annualAllowance}</TableCell>
                <TableCell>{t(`accrualType.${policy.accrualType.toLowerCase()}`)}</TableCell>
                <TableCell className="text-center">{policy.carryOverLimit}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(policy)}
                      data-testid={`policy-edit-${policy.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive focus:text-destructive"
                      onClick={() => onDelete(policy)}
                      data-testid={`policy-delete-${policy.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
